/**
 * Centralized marketing copy for landing pages.
 * ICP- and keyword-led (KEYWORD_CLUSTERS, docs/founding). Used by GTM Copy & Landing skill.
 * Each route: meta (title, description), hero (heading, subhead, CTAs), optional features/sections.
 *
 * Copy policy (audit-compliant): no unsubstantiated user counts; accuracy claims qualified.
 */

/** Approved trust line (no unverified user numbers). Use for hero. */
export const COPY_POLICY_TRUST_LINE = 'Trusted by creators and teams';

/** Final CTA social proof (no unverified user numbers). */
export const COPY_POLICY_FINAL_CTA = 'Join creators and teams who use Wisprs for accurate, fast transcriptions.';

/** Homepage trust strip above directory badges (factual; no unverified stats). */
export const HOME_DIRECTORY_BADGES_LABEL = 'Listed & featured on';

/**
 * Qualified accuracy claim for use site-wide (hero, how-it-works, features, footer, FAQ).
 * Do not imply a fixed % for every language/file; see docs/reference/STT_ACCURACY_AND_BENCHMARKS.md.
 */
export const COPY_POLICY_ACCURACY =
  'Excellent accuracy on clear audio; results vary by language, accent, and recording quality';

/** Nav bar CTAs (matches home hero primary CTA for consistency). */
export const NAV_CTAS = {
  login: 'Log in',
  primaryCta: 'Transcribe For Free →',
} as const;

export interface MetaCopy {
  title: string;
  description: string;
  keywords?: string[];
}

export interface HeroCopy {
  heading: string;
  subhead: string;
  /** Pain- and outcome-led bullets (not feature lists). Used for hero benefit list. */
  bullets?: string[];
  primaryCta: { label: string; href: string };
  secondaryCta?: { label: string; href: string };
}

export interface FeatureCopy {
  title: string;
  description: string;
}

export interface UseCaseCardCopy {
  id: string;
  name: string;
  description: string;
  features: string[];
  href: string;
}

export interface LandingCopy {
  meta: MetaCopy;
  hero: HeroCopy;
  features?: FeatureCopy[];
  useCaseCards?: UseCaseCardCopy[];
}

const home: LandingCopy = {
  meta: {
    title: 'Wisprs | Transcribe Audio & Video at the Speed of AI',
    description:
      'Speech-to-text software with excellent accuracy on clear audio, speaker recognition, and 100+ languages. Quality varies by language and recording conditions.',
    keywords: ['ai transcription software', 'speech to text software', 'audio to text converter', 'video transcription software', 'podcast transcription'],
  },
  hero: {
    heading: 'AI transcription software that gets it right.',
    subhead:
      'Speech to text with excellent accuracy on clear audio and speaker recognition. Quality varies by language, accent, and recording conditions.',
    bullets: [
      'Transcribe audio and video in 100+ languages',
      'Extract AI summaries, chapters, topics, and action items',
      'Export to TXT, SRT, VTT, MD, DOCX, or JSON',
      'Start for free. No credit card required.',
    ],
    primaryCta: { label: 'Transcribe For Free →', href: '/sign-up' },
    secondaryCta: { label: 'View Pricing', href: '/pricing' },
  },
};

const pricing: LandingCopy = {
  meta: {
    title: 'Pricing — AI Transcription Software Plans | Wisprs',
    description:
      'Transcription software pricing for creators, teams, and enterprises. Free tier, Pro, Studio, and Agency plans. Start free, upgrade when you need more.',
    keywords: ['transcription software pricing', 'speech to text pricing', 'ai transcription plans'],
  },
  hero: {
    heading: 'Transcription software pricing that scales with you',
    subhead:
      'Speech to text plans from free to team. 30 minutes/day free; then Pro, Studio, or Agency. No hidden fees.',
    primaryCta: { label: 'Start free', href: '/sign-up' },
    secondaryCta: { label: 'Compare plans', href: '#plans' },
  },
};

const creators: LandingCopy = {
  meta: {
    title: 'YouTube & Podcast Transcription for Creators | Wisprs',
    description:
      'Sync your whole YouTube channel or podcast into a searchable transcript library, then repurpose any episode into blog posts, threads, show notes, and subtitles. Transcription built for creators.',
    keywords: [
      'youtube channel transcription',
      'youtube video transcription',
      'podcast transcription',
      'podcast transcription software',
      'ai show notes generator',
      'content repurposing software',
      'transcribe podcast to text',
    ],
  },
  hero: {
    heading: 'Transcribe your whole channel into a searchable library',
    subhead:
      'Sync your entire YouTube channel or podcast back-catalog, then search any moment you ever said and repurpose it into blog posts, threads, and show notes. Excellent accuracy on clear audio, speaker labels, and SRT/VTT subtitles. Quality varies by language and recording conditions.',
    bullets: [
      'Sync a whole channel — your back-catalog becomes one searchable archive',
      'Search across every episode and jump to the exact moment',
      'Repurpose any episode into a blog post, thread, or show notes',
      'Export SRT/VTT subtitles for YouTube; speaker labels in 100+ languages',
    ],
    primaryCta: { label: 'Start transcribing', href: '/sign-up' },
    secondaryCta: { label: 'View pricing', href: '/pricing' },
  },
  features: [
    {
      title: 'Whole-channel sync',
      description:
        'Paste your YouTube @handle and we transcribe your back-catalog on your plan’s allowance — no uploading episode by episode.',
    },
    {
      title: 'Searchable library',
      description:
        'Every episode becomes searchable. Find any moment you said something across your whole archive and jump straight to it.',
    },
    {
      title: 'Repurpose in a click',
      description:
        'Turn one episode — or several at once — into a blog post, X thread, or show notes. Your back-catalog, working again.',
    },
    {
      title: 'Subtitles, speakers & languages',
      description:
        'Export accurate SRT/VTT subtitles with timestamps, automatic speaker labels for interviews, and 100+ languages.',
    },
  ],
};

const teams: LandingCopy = {
  meta: {
    title: 'Meeting Transcription & Batch Processing for Teams | Wisprs',
    description:
      'Batch transcription, workspace sharing, and file versioning for media agencies and PR firms. Meeting transcription software that scales.',
    keywords: ['meeting transcription', 'batch transcription', 'transcription for agencies', 'workspace sharing'],
  },
  hero: {
    heading: 'Meeting transcription software for teams & agencies',
    subhead:
      'Batch process uploads, share workspaces, and keep files organized. Built for media agencies, PR firms, and teams that need to scale.',
    primaryCta: { label: 'Get started', href: '/dashboard/upload' },
    secondaryCta: { label: 'View pricing', href: '/pricing' },
  },
  features: [
    {
      title: 'Batch processing',
      description: 'Upload multiple files and process in parallel. No more one-by-one for large projects.',
    },
    {
      title: 'Workspace sharing',
      description: 'Share folders and transcripts with your team. Keep client work organized and accessible.',
    },
    {
      title: 'Meeting transcription',
      description: 'Turn meetings, interviews, and calls into searchable transcripts and action items.',
    },
    {
      title: 'Export & integrate',
      description: 'Export TXT, DOCX, SRT, VTT. Use the API for custom workflows and integrations.',
    },
  ],
};

const enterprise: LandingCopy = {
  meta: {
    title: 'Enterprise Voice AI — API-First Transcription | Wisprs',
    description:
      'API-first transcription with SLAs, regional compliance, and data sovereignty. For education, legal, healthcare, and enterprise SaaS.',
    keywords: ['enterprise transcription', 'transcription API', 'compliance', 'SLAs', 'legal transcription', 'healthcare transcription'],
  },
  hero: {
    heading: 'Enterprise-grade transcription & voice AI',
    subhead:
      'API-first, SLAs, and regional compliance. For education, legal, healthcare, and SaaS. Data sovereignty and security by design.',
    primaryCta: { label: 'Contact sales', href: '/contact?topic=enterprise' },
    secondaryCta: { label: 'Try API', href: '/dashboard/upload' },
  },
  features: [
    {
      title: 'API-first',
      description: 'Unified STT API with intelligent routing. Integrate once, scale with predictable pricing.',
    },
    {
      title: 'SLAs & compliance',
      description: 'Uptime commitments and compliance options for regulated industries.',
    },
    {
      title: 'Data sovereignty',
      description: 'Control where data is processed. Options for regional deployment and retention policies.',
    },
    {
      title: 'Support & onboarding',
      description: 'Dedicated onboarding and support for enterprise deployments.',
    },
  ],
};

const sales: LandingCopy = {
  meta: {
    title: 'Sales Call to Follow-up Email & CRM Note | Wisprs',
    description:
      'Turn sales calls into follow-up emails and CRM notes in ~2 minutes. Sales call transcription with actionable next steps.',
    keywords: ['sales call transcription', 'follow-up email', 'CRM note', 'sales call summary'],
  },
  hero: {
    heading: 'Sales call → follow-up email + CRM note in ~2 minutes',
    subhead:
      'Upload a call and get a ready-to-send follow-up email and a formatted CRM note. No more manual note-taking.',
    primaryCta: { label: 'Upload one call', href: '/dashboard/upload' },
    secondaryCta: { label: 'Pro $25/mo', href: '/pricing' },
  },
  features: [
    { title: 'Follow-up email', description: 'Short, direct email ready to send (5–7 sentences).' },
    { title: 'Next steps', description: 'Checkbox list of action items from the call.' },
    { title: 'CRM note', description: 'Formatted note ready to paste into your CRM.' },
  ],
};

const features: LandingCopy = {
  meta: {
    title: 'Features — AI Transcription Software | Wisprs',
    description:
      'Speech to text with excellent accuracy on clear audio, speaker identification, 100+ languages, AI summaries, and export formats. See all Wisprs features.',
    keywords: [
      'ai transcription software',
      'speech to text software',
      'transcription features',
      'speaker identification',
    ],
  },
  hero: {
    heading: 'AI transcription software that actually delivers',
    subhead:
      'Excellent accuracy on clear audio, speaker recognition, 100+ languages, and export in every format you need. No credit card required. Built for creators, teams, and enterprises.',
    primaryCta: { label: 'Try free', href: '/sign-up' },
    secondaryCta: { label: 'Pricing', href: '/pricing' },
  },
};

const useCases: LandingCopy = {
  meta: {
    title: 'Use Cases — Creators, Teams, Sales & Enterprise | Wisprs',
    description:
      'AI transcription for podcasters, meeting transcription for teams, sales call follow-up, and enterprise API. Find your use case.',
    keywords: ['transcription use cases', 'podcast transcription', 'meeting transcription', 'sales call transcription'],
  },
  hero: {
    heading: 'Speech to text for creators, teams, sales & enterprise',
    subhead:
      'Podcast transcription, meeting transcription, sales call follow-up, and enterprise API. Get speech to text that fits your workflow.',
    primaryCta: { label: 'Get started', href: '/sign-up' },
    secondaryCta: { label: 'Pricing', href: '/pricing' },
  },
  useCaseCards: [
    {
      id: 'sales',
      name: 'Sales',
      description: 'Sales call transcription → follow-up email + CRM note in ~2 minutes',
      features: ['Follow-up Email', 'Next Steps', 'CRM Note'],
      href: '/sales',
    },
    {
      id: 'creators',
      name: 'Creators',
      description: 'Podcast transcription, show notes, and subtitles for YouTubers, podcasters, and streamers',
      features: ['Show Notes', 'Subtitles (SRT/VTT)', 'Repurposing'],
      href: '/creators',
    },
    {
      id: 'teams',
      name: 'Teams & Agencies',
      description: 'Meeting transcription and batch processing for media agencies and PR firms',
      features: ['Batch Processing', 'Workspace Sharing', 'Team Folders'],
      href: '/teams',
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'API-first transcription with SLAs and compliance for education, legal, and healthcare',
      features: ['API-First', 'SLAs & Compliance', 'Data Sovereignty'],
      href: '/enterprise',
    },
  ],
};

const faq: LandingCopy = {
  meta: {
    title: 'FAQ — AI Transcription & Speech to Text | Wisprs',
    description:
      'Common questions about Wisprs: accuracy, languages, pricing, security, and how to transcribe audio and video.',
    keywords: [
      'transcription FAQ',
      'speech to text FAQ',
      'ai transcription questions',
    ],
  },
  hero: {
    heading: 'Frequently asked questions',
    subhead: 'Transcription, pricing, security, and formats.',
    primaryCta: { label: 'Start transcribing', href: '/sign-up' },
    secondaryCta: { label: 'Pricing', href: '/pricing' },
  },
};

/** Marketing copy by route. Use for meta, hero, and optional features in landing pages. */
export const marketingCopy = {
  home,
  pricing,
  creators,
  teams,
  enterprise,
  sales,
  features,
  useCases,
  faq,
} as const;

export type MarketingCopyRoute = keyof typeof marketingCopy;

export function getLandingCopy(route: MarketingCopyRoute): LandingCopy {
  return marketingCopy[route];
}
